"use client";

import { Check } from "lucide-react";
import Link from "next/link";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils/index";

/** Shared row geometry, so every switcher popover's rows line up glyph for glyph. */
const ROW_BASE_CLASS = "flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-sm font-medium transition-colors";

interface FetchSwitcherRowProps {
  /** Leading glyph — a mascot, an org avatar, or an icon. */
  icon: ReactNode;
  /** Row label, truncated if it overflows. */
  label: string;
  /** Optional second line under the label, e.g. an email: muted, truncated independently. */
  sublabel?: string;
  /** Selected-row color scheme. "subtle" (gray highlight, indigo check — the tool and org-scope
   *  popovers) or "solid" (filled indigo, white check — the customer org switcher). */
  selectedTone?: "subtle" | "solid";
  /** Whether this row represents the current selection — shows the check glyph and the tone's
   *  highlighted background. */
  isSelected?: boolean;
  /** Whether this row is the keyboard-navigated position (arrow keys), independent of
   *  `isSelected` — a caller can be moving the highlight over rows that aren't the current
   *  selection. Shows the same subtle background `isSelected` does, with no check glyph. */
  isHighlighted?: boolean;
  /** Extra content shown before the check glyph (e.g. an event count or a keyboard shortcut). */
  trailing?: ReactNode;
  /** Renders the row as a `Link` instead of a button — for rows that navigate to a real page. */
  href?: string;
  /** Click handler for the button variant (ignored when `href` is set — a `Link` navigates instead). */
  onClick?: () => void;
  /** Renders an inert, muted row with no interaction — for a destination that isn't built yet. */
  disabled?: boolean;
  /** `href` variant only: exposes a quick-switch key to assistive tech without announcing it in
   *  the row's own name (e.g. a popover that lets you jump to row 2 by pressing "2"). */
  ariaKeyshortcuts?: string;
  /** DOM id — for a caller implementing the ARIA listbox pattern (`role="option"` + a search
   *  input's `aria-activedescendant` pointing at this id). Omit for a plain button/menu popover. */
  id?: string;
  /** Set to `"option"` when this row lives inside a `role="listbox"` combobox popover (adds
   *  `aria-selected`, tracking `isSelected`). Omit for a plain button/menu popover. */
  role?: "option";
}

/**
 * One row in a switcher popover's list (tool, org, or all-orgs): a leading glyph, a truncated
 * label, optional trailing content, and a trailing check when selected. Renders as a `<button>`
 * (in-place selection), a `Link` (real navigation), or an inert `<div>` (`disabled`, for a
 * destination that doesn't exist yet) — same look, three interaction shapes.
 *
 * @param props - See {@link FetchSwitcherRowProps}.
 * @returns The row.
 */
export function FetchSwitcherRow({
  icon,
  label,
  sublabel,
  selectedTone = "subtle",
  isSelected = false,
  isHighlighted = false,
  trailing,
  href,
  onClick,
  disabled = false,
  ariaKeyshortcuts,
  id,
  role,
}: FetchSwitcherRowProps): React.ReactElement {
  // Derived values
  const toneClass = disabled
    ? "text-gray-400 cursor-default"
    : selectedTone === "solid"
      ? isSelected
        ? "bg-indigo-600 text-white"
        : isHighlighted
          ? "bg-gray-50 text-gray-900"
          : "text-gray-700 hover:bg-gray-50"
      : isSelected || isHighlighted
        ? "bg-gray-50 text-gray-900"
        : "text-gray-700 hover:bg-gray-50 hover:text-gray-900";
  const rowClassName = cn(ROW_BASE_CLASS, toneClass);

  const content = (
    <>
      <span className="flex min-w-0 flex-1 items-center gap-2">
        {icon}
        <span className="flex min-w-0 flex-1 flex-col text-left">
          <span className="truncate">{label}</span>
          {/* `opacity-70` (not a fixed gray) so this stays legible against `selectedTone="solid"`'s
           *  white-on-indigo selected row, not just the default gray-on-white. */}
          {sublabel && <span className="truncate text-xs font-medium opacity-70">{sublabel}</span>}
        </span>
      </span>
      {(trailing || isSelected) && (
        <span className="flex shrink-0 items-center gap-2">
          {trailing}
          {isSelected && !disabled && (
            <Check
              className={cn("size-3.5 shrink-0", selectedTone === "subtle" && "text-indigo-500")}
              aria-hidden="true"
            />
          )}
        </span>
      )}
    </>
  );

  // Render
  if (disabled) {
    return (
      <div id={id} aria-disabled="true" className={rowClassName}>
        {content}
      </div>
    );
  }

  if (href) {
    return (
      <Link
        id={id}
        href={href}
        onClick={onClick}
        aria-current={isSelected ? "page" : undefined}
        aria-keyshortcuts={ariaKeyshortcuts}
        className={rowClassName}
      >
        {content}
      </Link>
    );
  }

  if (role === "option") {
    // Not `<button role="option">` — that pairs conflicting semantics (see accessibility rules).
    // Not `tabIndex`-focusable either: this row lives in an `aria-activedescendant` combobox,
    // where real DOM focus stays on the search input throughout and the keyboard-navigated
    // ("active") option is conveyed by that attribute, not by moving focus row to row.
    // `aria-selected` tracks `isSelected` (the current value), independent of `isHighlighted`
    // (the keyboard position) — the two can differ, e.g. arrowing over a row that isn't current.
    return (
      <div id={id} role="option" aria-selected={isSelected} onClick={onClick} className={rowClassName}>
        {content}
      </div>
    );
  }

  return (
    <button type="button" onClick={onClick} aria-current={isSelected ? "true" : undefined} className={rowClassName}>
      {content}
    </button>
  );
}
